import { TierList, Image, AppSettings, TierItem } from "@/types/tier-list";

const DB_NAME = "TierListDB";
const DB_VERSION = 3;

// Store names
const TIER_LISTS_STORE = "tierLists";
const IMAGES_STORE = "images";
const TIER_ITEMS_STORE = "tierItems";
const SETTINGS_STORE = "settings";

// Initialize IndexedDB
export function initDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create tier lists store
      if (!db.objectStoreNames.contains(TIER_LISTS_STORE)) {
        db.createObjectStore(TIER_LISTS_STORE, { keyPath: "id" });
      }

      // Create images store
      if (!db.objectStoreNames.contains(IMAGES_STORE)) {
        const imageStore = db.createObjectStore(IMAGES_STORE, { keyPath: "id" });
        imageStore.createIndex("tierListId", "tierListId", { unique: false });
      }

      // Create tier items store
      if (!db.objectStoreNames.contains(TIER_ITEMS_STORE)) {
        const tierItemsStore = db.createObjectStore(TIER_ITEMS_STORE, { keyPath: "id" });
        tierItemsStore.createIndex("tierListId", "tierListId", { unique: false });
        tierItemsStore.createIndex("tierId", "tierId", { unique: false });
      }

      // Create settings store
      if (!db.objectStoreNames.contains(SETTINGS_STORE)) {
        db.createObjectStore(SETTINGS_STORE, { keyPath: "key" });
      }
    };
  });
}

// Tier Lists Operations
export async function saveTierList(tierList: TierList): Promise<void> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([TIER_LISTS_STORE], "readwrite");
    const store = transaction.objectStore(TIER_LISTS_STORE);
    const request = store.put(tierList);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function getTierList(id: string): Promise<TierList | undefined> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([TIER_LISTS_STORE], "readonly");
    const store = transaction.objectStore(TIER_LISTS_STORE);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getAllTierLists(): Promise<TierList[]> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([TIER_LISTS_STORE], "readonly");
    const store = transaction.objectStore(TIER_LISTS_STORE);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function deleteTierList(id: string): Promise<void> {
  // Get all related images and tier items first
  const images = await getImagesByTierListId(id);
  const items = await getTierItemsByTierListId(id);

  // Delete all related data in parallel
  await Promise.all([
    ...images.map((img) => deleteImage(img.id)),
    ...items.map((item) => deleteTierItem(item.id)),
  ]);

  // Finally delete the tier list itself
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([TIER_LISTS_STORE], "readwrite");
    const store = transaction.objectStore(TIER_LISTS_STORE);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// Image Operations
export async function saveImage(image: Image): Promise<void> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([IMAGES_STORE], "readwrite");
    const store = transaction.objectStore(IMAGES_STORE);
    const request = store.put(image);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function getImage(id: string): Promise<Image | undefined> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([IMAGES_STORE], "readonly");
    const store = transaction.objectStore(IMAGES_STORE);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function deleteImage(id: string): Promise<void> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([IMAGES_STORE], "readwrite");
    const store = transaction.objectStore(IMAGES_STORE);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function getImagesByTierListId(tierListId: string): Promise<Image[]> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([IMAGES_STORE], "readonly");
    const store = transaction.objectStore(IMAGES_STORE);
    const index = store.index("tierListId");
    const request = index.getAll(tierListId);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Helper function to resolve image to URL
export async function resolveImage(imageId: string): Promise<string | null> {
  const image = await getImage(imageId);
  if (!image) return null;
  return URL.createObjectURL(image.blob);
}

// Settings Operations
export async function getLastUsedTierListId(): Promise<string | undefined> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SETTINGS_STORE], "readonly");
    const store = transaction.objectStore(SETTINGS_STORE);
    const request = store.get("lastUsedTierListId");

    request.onsuccess = () => resolve(request.result?.value);
    request.onerror = () => reject(request.error);
  });
}

export async function setLastUsedTierListId(id: string): Promise<void> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SETTINGS_STORE], "readwrite");
    const store = transaction.objectStore(SETTINGS_STORE);
    const request = store.put({ key: "lastUsedTierListId", value: id });

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// Tier Items Operations
export async function saveTierItem(item: TierItem): Promise<void> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([TIER_ITEMS_STORE], "readwrite");
    const store = transaction.objectStore(TIER_ITEMS_STORE);
    const request = store.put(item);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function getTierItem(id: string): Promise<TierItem | undefined> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([TIER_ITEMS_STORE], "readonly");
    const store = transaction.objectStore(TIER_ITEMS_STORE);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getTierItemsByTierListId(tierListId: string): Promise<TierItem[]> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([TIER_ITEMS_STORE], "readonly");
    const store = transaction.objectStore(TIER_ITEMS_STORE);
    const index = store.index("tierListId");
    const request = index.getAll(tierListId);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function deleteTierItem(id: string): Promise<void> {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([TIER_ITEMS_STORE], "readwrite");
    const store = transaction.objectStore(TIER_ITEMS_STORE);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function updateTierItemPlacement(itemId: string, tierId?: string): Promise<void> {
  const item = await getTierItem(itemId);
  if (!item) throw new Error("Item not found");

  item.tierId = tierId;
  await saveTierItem(item);
}
